import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getListingsByUser,
  getPurchasesByUser,
  getTasksByDeals,
  getCommunicationLogs,
  toClientDeals,
} from '../lib/directus';
import type { ClientDeal, ClientTask, ClientCommunicationLog } from '../lib/directus';
import ClientLayout from '../components/ClientLayout';

export default function DashboardPage() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState<ClientDeal[]>([]);
  const [tasks, setTasks] = useState<ClientTask[]>([]);
  const [messages, setMessages] = useState<ClientCommunicationLog[]>([]);

  useEffect(() => {
    async function loadDashboard() {
      if (!user || !token) return;

      try {
        const [listings, purchases] = await Promise.all([
          getListingsByUser(user.id, token),
          getPurchasesByUser(user.id, token),
        ]);

        const clientDeals = toClientDeals(listings, purchases);
        setDeals(clientDeals);

        const dealIds = clientDeals.map((d) => d.id);
        const [tasksData, logsData] = await Promise.all([
          getTasksByDeals(dealIds, token),
          getCommunicationLogs(user.id, token),
        ]);

        setTasks(tasksData);
        setMessages(logsData);
      } catch (error) {
        console.error('Error loading dashboard:', error);
      } finally {
        setLoading(false);
      }
    }

    void loadDashboard();
  }, [user, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#DDD6CE]">
        <div className="text-[#134052] text-xl font-semibold">Loading your dashboard...</div>
      </div>
    );
  }

  const activeDeal = deals.find((d) => d.status !== 'closed' && d.status !== 'terminated') ?? deals[0];

  const formatAddress = (deal: ClientDeal) => {
    const parts = [deal.street_address, deal.city, deal.state, deal.zip_code].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : deal.property_address;
  };

  return (
    <ClientLayout user={user}>
      {/* Welcome */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-[#134052] mb-2">
          Welcome back, {user?.first_name}
        </h2>
        <p className="text-gray-600">Here&apos;s what&apos;s happening with your property.</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard label="Active Deals" value={deals.filter((d) => d.status !== 'closed' && d.status !== 'terminated').length} color="bg-[#134052]" />
        <StatCard label="Pending Tasks" value={tasks.length} color="bg-[#C5A880]" />
        <StatCard label="Recent Messages" value={messages.length} color="bg-[#134052]" />
      </div>

      {/* Content grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Deal */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-[#134052] to-[#1a5368] px-6 py-4">
            <h3 className="text-xl font-bold text-white">Your Active Deal</h3>
          </div>
          <div className="p-6">
            {activeDeal ? (
              <div>
                <div className="mb-4 flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#DDD6CE] text-[#134052]">
                    {activeDeal.type === 'listing' ? 'Selling' : 'Buying'}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#C5A880] text-white">
                    {activeDeal.status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                  </span>
                </div>

                <div className="bg-gradient-to-br from-[#DDD6CE] to-[#e8e4dc] rounded-lg p-5 mb-4">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Property Address</p>
                  <p className="text-lg font-semibold text-[#134052]">
                    {formatAddress(activeDeal)}
                  </p>
                </div>

                {activeDeal.price != null && (
                  <div className="flex items-center pt-4 border-t border-gray-100">
                    <div>
                      <p className="text-xs text-gray-500">
                        {activeDeal.type === 'listing' ? 'List Price' : 'Asking Price'}
                      </p>
                      <p className="text-lg font-semibold text-[#134052]">
                        ${activeDeal.price.toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">No active deals at the moment.</p>
            )}
          </div>
        </div>

        {/* Tasks */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="bg-gradient-to-r from-[#C5A880] to-[#d4b890] px-6 py-4">
            <h3 className="text-xl font-bold text-white">Your Tasks</h3>
          </div>
          <div className="p-6">
            {tasks.length > 0 ? (
              <ul className="space-y-3">
                {tasks.slice(0, 5).map((task) => (
                  <li key={task.id} className="flex items-start border-l-3 border-[#C5A880] pl-4 py-2 hover:bg-gray-50 rounded-r transition-colors">
                    <div className="flex-1">
                      <p className="font-medium text-[#134052] text-sm">{task.title}</p>
                      {task.due_date && (
                        <p className="text-xs text-gray-500 mt-1">
                          Due: {new Date(task.due_date).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-sm text-center py-8">No pending tasks.</p>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-[#134052] to-[#1a5368] px-6 py-4">
          <h3 className="text-xl font-bold text-white">Recent Messages</h3>
        </div>
        <div className="p-6">
          {messages.length > 0 ? (
            <div className="space-y-4">
              {messages.slice(0, 3).map((msg) => (
                <div key={msg.id} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-[#C5A880] flex items-center justify-center text-white text-xs font-semibold">
                        {msg.type?.[0]?.toUpperCase() ?? 'M'}
                      </div>
                      <div className="ml-3">
                        <p className="font-medium text-[#134052] text-sm">
                          {msg.subject ?? msg.type}
                        </p>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                          {msg.direction}
                        </span>
                      </div>
                    </div>
                    {msg.date_created && (
                      <p className="text-xs text-gray-500">
                        {new Date(msg.date_created).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  {msg.body && (
                    <p className="text-sm text-gray-700 ml-11 line-clamp-2">{msg.body}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm text-center py-8">No messages yet.</p>
          )}
        </div>
      </div>
    </ClientLayout>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="text-3xl font-bold text-[#134052] mt-2">{value}</p>
        </div>
        <div className={`h-12 w-12 ${color} rounded-lg`} />
      </div>
    </div>
  );
}
